// Button — the one button. Token-driven, accessible (focus-visible ring),
// light + dark. Renders <button> by default, or <a> when `href` is set.
//
//   <Button onClick={save}>Save</Button>
//   <Button variant="secondary" size="sm" icon={<Icon name="plus" size={15}/>}>Add</Button>
//   <Button variant="danger" loading={busy} onClick={remove}>Delete</Button>
//   <Button variant="ghost" href="/help">Help</Button>

import { ButtonHTMLAttributes, AnchorHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Show a spinner and block interaction. */
  loading?: boolean;
  /** Leading icon element (e.g. <Icon name="plus" />). */
  icon?: ReactNode;
  /** Trailing icon element. */
  iconRight?: ReactNode;
  /** Full-width. */
  block?: boolean;
  /** Icon-only (square padding). Provide aria-label for accessibility. */
  iconOnly?: boolean;
  children?: ReactNode;
}

type ButtonAsButton = CommonProps & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> & { href?: undefined; className?: string };
type ButtonAsLink = CommonProps & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className'> & { href: string; className?: string };
export type ButtonProps = ButtonAsButton | ButtonAsLink;

function classes(p: CommonProps & { className?: string }): string {
  return [
    'ui-btn',
    `ui-btn--${p.variant ?? 'primary'}`,
    p.size && p.size !== 'md' ? `ui-btn--${p.size}` : '',
    p.block ? 'ui-btn--block' : '',
    p.iconOnly ? 'ui-btn--icon' : '',
    p.className ?? '',
  ].filter(Boolean).join(' ');
}

export default function Button(props: ButtonProps) {
  const { variant, size, loading, icon, iconRight, block, iconOnly, children, ...rest } = props;
  const inner = (
    <>
      {loading ? <span className="ui-btn__spinner" aria-hidden /> : icon}
      {children != null && children !== '' ? <span>{children}</span> : null}
      {!loading && iconRight ? iconRight : null}
    </>
  );

  if ('href' in props && props.href !== undefined) {
    const { href, ...anchorRest } = rest as AnchorHTMLAttributes<HTMLAnchorElement>;
    return (
      <a href={href} className={classes(props)} aria-busy={loading || undefined} {...anchorRest}>
        {inner}
      </a>
    );
  }

  const { disabled, type, ...btnRest } = rest as ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button
      type={type ?? 'button'}
      className={classes(props)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...btnRest}
    >
      {inner}
    </button>
  );
}
